"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Checkbox, Button, Tabs, Modal, message } from "antd";
import { SettingOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import {
  Cache_Category_Key,
  Cache_Tag_Key,
  getCacheCategories,
  getCacheTags,
  Preferred_Category_Key,
  Preferred_Tag_Key,
} from "../../utils/apiSample/recommendation";
import {
  userGetBookCategories,
  userGetBookTags,
} from "../../apiservice/userService";
import type {
  IBookCategoryData,
  IBookTagData,
} from "../../apiservice/userService.type";
import { appZIndex } from "../../utils/appconst";

const { TabPane } = Tabs;

// Define the configuration options
export interface PreferencesConfig {
  showBackButton?: boolean;
  backButtonLink?: string;
  displayMode?: "tabs" | "separate";
  activeIndex?: string;
  afterSaveRedirect?: string;
  modalTitle?: string;
  isModal?: boolean;
  onClose?: () => void;
  onPreferencesUpdated?: () => void;
}

export const PreferencesManager = ({
  showBackButton = false,
  backButtonLink = "/",
  displayMode = "tabs",
  activeIndex = "categories",
  afterSaveRedirect = "/",
  modalTitle = "Personalize Your Experience",
  isModal = false,
  onClose,
  onPreferencesUpdated,
}: PreferencesConfig) => {
  const navigate = useNavigate();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string>(activeIndex);

  const [allData, setAllData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    let allCategories: IBookCategoryData[] = [];
    let allTags: IBookTagData[] = [];

    try {
      const cacheData = await getCacheCategories();
      allCategories = cacheData;

      const cacheDataTag = await getCacheTags();
      allTags = cacheDataTag;

      setLoading(true);
      setError(null);

      // Make all API calls in parallel
      const [loadedCategories, loadedTag] = await Promise.all([
        userGetBookCategories(),
        userGetBookTags(),
      ]);

      allCategories = loadedCategories?.data;
      allTags = loadedTag?.data;

      localStorage.setItem(
        Cache_Category_Key,
        JSON.stringify(loadedCategories?.data)
      );

      localStorage.setItem(Cache_Tag_Key, JSON.stringify(loadedTag?.data));
    } catch (err) {
      console.error("Error fetching books:", err);
      setError("Failed to load books. Please try again later.");
    } finally {
      setLoading(false);
    }

    setAllData({
      allCategories,
      allTags,
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  // Load saved preferences on component mount
  useEffect(() => {
    const savedCategories = localStorage.getItem(Preferred_Category_Key);
    const savedTags = localStorage.getItem(Preferred_Tag_Key);

    if (savedCategories) {
      setSelectedCategories(JSON.parse(savedCategories));
    }

    if (savedTags) {
      setSelectedTags(JSON.parse(savedTags));
    }
  }, []);

  // Handle category selection
  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategories((prev) => {
      if (prev.includes(categoryId)) {
        return prev.filter((id) => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  // Handle tag selection
  const handleTagChange = (tag: string) => {
    setSelectedTags((prev) => {
      if (prev.includes(tag)) {
        return prev.filter((t) => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
  };

  // Save preferences and navigate
  const handleSave = () => {
    localStorage.setItem(
      Preferred_Category_Key,
      JSON.stringify(selectedCategories)
    );
    localStorage.setItem(Preferred_Tag_Key, JSON.stringify(selectedTags));
    message.success("Preferences saved successfully");

    // Notify parent component that preferences were updated
    if (onPreferencesUpdated) {
      onPreferencesUpdated();
    }

    // Handle modal close if in modal mode
    if (isModal && onClose) {
      onClose();
    } else {
      // Navigate to the specified redirect URL
      navigate(afterSaveRedirect);
    }
  };

  // Handle back button click
  const handleBack = () => {
    if (isModal && onClose) {
      onClose();
    } else {
      navigate(backButtonLink);
    }
  };

  // Render categories section
  const renderCategories = () => (
    <div className={`${isModal ? "" : "p-6"}`}>
      <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-stone-100">
        Categories for you
      </h3>
      <p className="text-gray-600 dark:text-stone-400 mb-4">
        Select the best categories for you.
      </p>

      <div
        className={`space-y-2 ${
          isModal ? "max-h-[300px] overflow-y-auto" : ""
        }`}
      >
        {((allData?.allCategories as IBookCategoryData[]) || []).map(
          (category) => (
            <div
              key={category.id}
              className={`flex items-center p-3 rounded-lg border ${
                selectedCategories.includes(category.id + "")
                  ? "border-indigo-600 bg-indigo-50 dark:border-indigo-500 dark:bg-indigo-900/30"
                  : "border-gray-200 bg-gray-50 dark:border-stone-700 dark:bg-stone-800"
              }`}
            >
              <div
                className={`mr-3 ${
                  selectedCategories.includes(category.id + "")
                    ? "text-indigo-700 dark:text-indigo-300"
                    : "text-gray-700 dark:text-stone-300"
                }`}
              ></div>
              <span
                className={`flex-grow ${
                  selectedCategories.includes(category.id + "")
                    ? "text-indigo-800 dark:text-indigo-200"
                    : "text-gray-800 dark:text-stone-200"
                }`}
              >
                {category.title}
              </span>
              <Checkbox
                checked={selectedCategories.includes(category.id + "")}
                onChange={() => handleCategoryChange(category.id + "")}
                className="ml-2 [&_.ant-checkbox-inner]:dark:bg-stone-700 [&_.ant-checkbox-inner]:dark:border-stone-600 [&_.ant-checkbox-checked_.ant-checkbox-inner]:dark:bg-indigo-600 [&_.ant-checkbox-checked_.ant-checkbox-inner]:dark:border-indigo-600"
              />
            </div>
          )
        )}
      </div>

      <div className="p-4 border-t dark:border-stone-700">
        <Button
          type="primary"
          block
          size="large"
          onClick={handleSave}
          className="bg-indigo-700 hover:bg-indigo-600 dark:bg-indigo-600 dark:hover:bg-indigo-500 h-12"
        >
          Continue
        </Button>
      </div>
    </div>
  );

  // Render tags section
  const renderTags = () => (
    <div className={`${isModal ? "" : "p-6"}`}>
      <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-stone-100">
        Tags for you
      </h3>
      <p className="text-gray-600 dark:text-stone-400 mb-4">
        Select tags that interest you.
      </p>

      <div
        className={`flex flex-wrap gap-2 ${
          isModal ? "max-h-[300px] overflow-y-auto" : ""
        }`}
      >
        {((allData?.allTags as IBookTagData[]) || []).map((tag, index) => (
          <div
            key={index}
            onClick={() => handleTagChange(tag.title)}
            className={`px-4 py-2 rounded-full cursor-pointer transition-colors ${
              selectedTags.includes(tag.title)
                ? "bg-indigo-600 text-white dark:bg-indigo-500 dark:text-white"
                : "bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-stone-700 dark:text-stone-200 dark:hover:bg-stone-600"
            }`}
          >
            {tag.title}
          </div>
        ))}
      </div>

      <div className="p-4 border-t dark:border-stone-700">
        <Button
          type="primary"
          block
          size="large"
          onClick={handleSave}
          className="bg-indigo-700 hover:bg-indigo-600 dark:bg-indigo-600 dark:hover:bg-indigo-500 h-12"
        >
          Continue
        </Button>
      </div>
    </div>
  );

  // Content to render based on display mode
  const renderContent = () => {
    if (displayMode === "tabs") {
      return (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className={`${
            isModal ? "mt-4" : "mb-6"
          } [&_.ant-tabs-nav]:bg-transparent [&_.ant-tabs-nav]:dark:bg-transparent [&_.ant-tabs-tab]:text-gray-700 [&_.ant-tabs-tab]:dark:text-stone-300 [&_.ant-tabs-tab-active]:text-indigo-600 [&_.ant-tabs-tab-active]:dark:text-indigo-400 [&_.ant-tabs-ink-bar]:bg-indigo-600 [&_.ant-tabs-ink-bar]:dark:bg-indigo-400 [&_.ant-tabs-content-holder]:bg-transparent [&_.ant-tabs-content-holder]:dark:bg-transparent`}
          centered={!isModal}
        >
          <TabPane tab="Categories" key="categories">
            {renderCategories()}
          </TabPane>

          <TabPane tab="Tags" key="tags">
            {renderTags()}
          </TabPane>
        </Tabs>
      );
    } else {
      // Separate mode - show only the active component
      return activeTab === "categories" ? renderCategories() : renderTags();
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center text-gray-900 dark:text-stone-100">
        {" "}
        Loading Tag details...
      </div>
    );
  }

  if (error || !allData) {
    return (
      <div className="p-4 text-center text-red-500 dark:text-red-400">
        {error || "Tag not found"}
      </div>
    );
  }

  // Render the component based on whether it's in modal mode or not
  if (isModal) {
    return (
      <div className="dark:bg-stone-900 dark:text-stone-100">
        {renderContent()}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-stone-900 flex flex-col">
      {showBackButton && (
        <div className="p-4 border-b dark:border-stone-700">
          <Button
            icon={<ArrowLeftOutlined className="dark:text-stone-300" />}
            onClick={handleBack}
            type="text"
            className="flex items-center text-gray-700 dark:text-stone-300 hover:text-blue-600 dark:hover:text-blue-400"
          >
            Back
          </Button>
        </div>
      )}

      <div className="p-6 flex-1">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold mb-1 text-gray-900 dark:text-stone-100">
            {modalTitle}
          </h1>
          <p className="text-gray-600 dark:text-stone-400">
            Select your preferences to get tailored recommendations
          </p>
        </div>

        {renderContent()}
      </div>
    </div>
  );
};

// Modal wrapper for the PreferencesManager
export const PreferencesModal = ({
  isOpen,
  onClose,
  onPreferencesUpdated,
  activeIndex = "categories",
  modalTitle = "Personalize Your Experience",
}: {
  isOpen: boolean;
  onClose: () => void;
  onPreferencesUpdated?: () => void;
  activeIndex?: string;
  modalTitle?: string;
}) => {
  return (
    <Modal
      title={
        <span className="text-gray-900 dark:text-stone-100">{modalTitle}</span>
      }
      open={isOpen}
      onCancel={onClose}
      width={600}
      zIndex={appZIndex.modal}
      footer={[
        <Button
          key="cancel"
          onClick={onClose}
          className="dark:bg-stone-700 dark:border-stone-600 dark:text-stone-200 dark:hover:bg-stone-600"
        >
          Cancel
        </Button>,
      ]}
      className="[&_.ant-modal-content]:bg-white [&_.ant-modal-content]:dark:bg-stone-900 [&_.ant-modal-header]:bg-white [&_.ant-modal-header]:dark:bg-stone-900 [&_.ant-modal-header]:border-b [&_.ant-modal-header]:border-gray-200 [&_.ant-modal-header]:dark:border-stone-700 [&_.ant-modal-body]:bg-white [&_.ant-modal-body]:dark:bg-stone-900 [&_.ant-modal-body]:p-0"
    >
      <PreferencesManager
        isModal={true}
        onClose={onClose}
        onPreferencesUpdated={onPreferencesUpdated}
        activeIndex={activeIndex}
        modalTitle={modalTitle}
      />
    </Modal>
  );
};

// Button component that opens the preferences modal
export const PreferencesButton = ({
  onPreferencesUpdated,
  activeIndex,
  buttonText,
  modalTitle,
}: {
  onPreferencesUpdated?: () => void;
  activeIndex?: string;
  buttonText?: string;
  modalTitle?: string;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Button
        icon={<SettingOutlined />}
        onClick={() => setIsModalOpen(true)}
        className="flex items-center text-gray-700 dark:text-stone-300 hover:text-blue-600 dark:hover:text-blue-400 dark:bg-transparent dark:border-transparent"
        type="text"
      >
        {buttonText || "Preferences"}
      </Button>

      <PreferencesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onPreferencesUpdated={onPreferencesUpdated}
        activeIndex={activeIndex}
        modalTitle={modalTitle}
      />
    </>
  );
};

export default PreferencesManager;
